/**
 * The Courses tab, /lms/courses (plan docs/plans/lms-plan.md section 13 tab 2;
 * 7.2, 8.4, 18.17, 18.22; WP-42; R3, R9, R24).
 *
 * The network's catalog grouped by category (compliance, clinical skills,
 * best practices, SOPs, platform, custom), each course with its title,
 * estimated minutes, validity and the person's status chip from GET
 * /me/status (compliant until {date}, due {date}, overdue, not started); a
 * search box and category chips narrow it; a row opens the course page
 * (CoursePage.tsx). Retired courses are never listed (18.17: the route lists
 * active courses with a published version, and isListable re-checks).
 *
 * Two reads, together, again on every site switch (every /api/lms call
 * carries X-Site-Id): GET /courses (all pages) and GET /me/status. The
 * catalog failing fails the screen with a Retry (G10); the status failing
 * keeps the catalog on screen and says the chips could not be loaded.
 *
 * ui primitives by their own file path, never the barrel (2.3a).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, BookOpen, Loader2, SearchX } from 'lucide-react';
import PageHeader, { RefreshButton } from '../../../ui/PageHeader';
import EmptyState from '../../../ui/EmptyState';
import SearchInput from '../../../ui/SearchInput';
import FilterChip from '../../../ui/FilterChip';
import { outlineBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { useLms } from '../../LmsProvider';
import { apiErrorMessage } from '../../api/client';
import type { CourseListItem } from '../../api/courses';
import { getMyStatus } from '../../api/completions';
import type { MyCourseStatus } from '../../api/completions';
import { lmsPageCls } from '../../components/layout/page';
import { noticeCls } from '../../components/player/styles';
import { lmsTab } from '../config';
import {
  courseHref,
  groupByCategory,
  isListable,
  kindLabel,
  loadCatalog,
  matchesSearch,
  minutesShort,
  validityText,
} from './catalog';
import { CourseStatusChip } from './CourseStatusChip';

const TAB = lmsTab('courses');

/** The category chip value for "every category". */
const ALL = 'all';

type Load =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ready'; courses: CourseListItem[]; status: Record<string, MyCourseStatus> | null; statusError: string | null };

const groupHeadingCls = 'text-[10px] font-semibold uppercase tracking-widest text-gray-600 dark:text-gray-400';

export function CoursesTab() {
  const { siteId } = useLms();
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>(ALL);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    if (!siteId) {
      setLoad({ state: 'error', message: 'Select a site to see its courses.' });
      return undefined;
    }
    setLoad({ state: 'loading' });
    Promise.allSettled([loadCatalog(), getMyStatus()]).then(([catalog, status]) => {
      if (cancelled) return;
      if (catalog.status === 'rejected') {
        setLoad({ state: 'error', message: apiErrorMessage(catalog.reason, 'The course catalog could not be loaded.') });
        return;
      }
      const courses = catalog.value.filter(isListable);
      if (status.status === 'rejected') {
        setLoad({
          state: 'ready',
          courses,
          status: null,
          statusError: apiErrorMessage(status.reason, 'Your status on each course could not be loaded.'),
        });
        return;
      }
      const map = status.value && typeof status.value.courses === 'object' && status.value.courses ? status.value.courses : {};
      setLoad({ state: 'ready', courses, status: map, statusError: null });
    });
    return () => {
      cancelled = true;
    };
  }, [siteId, tick]);

  const courses = useMemo(() => (load.state === 'ready' ? load.courses : []), [load]);
  const searched = useMemo(() => courses.filter((c) => matchesSearch(c, query)), [courses, query]);
  const categoryGroups = useMemo(() => groupByCategory(searched), [searched]);
  // A chosen category that the list no longer holds (a site switch, a search) reads as "all".
  const activeCategory = category !== ALL && categoryGroups.some((g) => g.category === category) ? category : ALL;
  const groups = activeCategory === ALL ? categoryGroups : categoryGroups.filter((g) => g.category === activeCategory);

  const header = (
    <PageHeader
      title={TAB.title}
      icon={TAB.icon}
      count={load.state === 'ready' ? load.courses.length : null}
      subtitle={TAB.description}
      actions={<RefreshButton onClick={reload} loading={load.state === 'loading'} label="Reload the catalog" />}
    />
  );

  if (load.state === 'loading') {
    return (
      <div className={lmsPageCls} data-testid="lms-courses-tab">
        {header}
        <div role="status" className="flex items-center gap-2 py-8 text-sm text-gray-600 dark:text-gray-400" data-testid="lms-courses-loading">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Loading the catalog...
        </div>
      </div>
    );
  }

  if (load.state === 'error') {
    return (
      <div className={lmsPageCls} data-testid="lms-courses-tab">
        {header}
        <div className={noticeCls('red')} role="alert" data-testid="lms-courses-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
            <span>{load.message}</span>
            {siteId && (
              <button type="button" className={secondaryBtnCls} onClick={reload}>
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={lmsPageCls} data-testid="lms-courses-tab">
      {header}

      {load.statusError && (
        <div className={noticeCls('amber')} role="status" data-testid="lms-courses-status-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
            <span>{`${load.statusError} The catalog is shown without your status.`}</span>
            <button type="button" className={outlineBtnCls()} onClick={reload}>
              Retry
            </button>
          </div>
        </div>
      )}

      {courses.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No courses yet"
          hint="Courses appear here once a Learning Center admin publishes them."
          variant="card"
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder="Search courses"
              label="Search courses"
              className="w-full sm:w-72"
            />
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Category" data-testid="lms-course-categories">
              <FilterChip active={activeCategory === ALL} onClick={() => setCategory(ALL)}>
                {`All (${searched.length})`}
              </FilterChip>
              {categoryGroups.map((g) => (
                <FilterChip key={g.category} active={activeCategory === g.category} onClick={() => setCategory(g.category)}>
                  {`${g.label} (${g.courses.length})`}
                </FilterChip>
              ))}
            </div>
          </div>

          {groups.length === 0 ? (
            <EmptyState
              icon={SearchX}
              title="No courses match your search"
              hint="Try another word, or clear the search to see the whole catalog."
              action={
                <button type="button" className={secondaryBtnCls} onClick={() => setQuery('')}>
                  Clear search
                </button>
              }
              variant="card"
            />
          ) : (
            <div className="space-y-5">
              {groups.map((g) => (
                <section key={g.category} aria-labelledby={`lms-course-group-${g.category}`} data-testid="lms-course-group" data-category={g.category}>
                  <h2 id={`lms-course-group-${g.category}`} className={`mb-2 ${groupHeadingCls}`}>
                    {`${g.label} (${g.courses.length})`}
                  </h2>
                  <ul className="divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
                    {g.courses.map((course) => (
                      <CourseRow key={course.id} course={course} status={load.status} />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CourseRow({ course, status }: { course: CourseListItem; status: Record<string, MyCourseStatus> | null }) {
  // The category is the group heading above the row, so the row does not repeat it.
  const meta = [
    course.kind === 'quiz' ? null : kindLabel(course.kind),
    minutesShort(course.estimated_minutes),
    validityText(course.validity_months),
  ].filter((part): part is string => Boolean(part));
  return (
    <li data-testid="lms-course-row" data-course-id={course.id}>
      <Link
        to={courseHref(course.id)}
        className="flex min-h-[44px] flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-4 py-3 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:hover:bg-gray-700/50"
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100" data-testid="lms-course-title">
            {course.title}
          </div>
          <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-400" data-testid="lms-course-meta">
            {meta.join(' · ')}
          </div>
        </div>
        {status && <CourseStatusChip cell={status[course.id]} />}
      </Link>
    </li>
  );
}

export default CoursesTab;
